// Global baseline overlay: the user picks benchmarks (SP500, tech ETFs, gold,
// treasuries) once; the choice persists (localStorage) and every equity plot
// overlays them. Baselines are served growth-of-$1 and rebased onto each plot
// at the baseline's first in-window date (matching the primary curve there).
import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api.js";
import { yearFrac } from "./dates.js";
import { nextSession } from "./components/Freshness.jsx";

const Ctx = createContext({ available: [], series: [], selected: [], toggle: () => {} });
const KEY = "cc-baselines";

// FR9: a fair, BETA-MATCHED S&P benchmark. PEAD's hedged long/short book runs β≈0.66 to SPY (the
// Beta study), so a fully-invested S&P (β=1) is an unfairly high bar. Derive a passive S&P line
// dampened to β=0.66 — each S&P daily return × 0.66, compounded from $1 — purely client-side from
// the existing SP500 growth-of-$1 series (no backend change), injected as another selectable
// benchmark. Honest + defined: this is "a passive S&P position scaled to the strategy's market beta".
const SPY_BETA = 0.66;
function withBetaMatched(baselines, series) {
  // guard: only inject when the real SP500 curve is present; otherwise leave everything untouched.
  if (!Array.isArray(series) || !series.length || !baselines?.some((b) => b.key === "SP500")) {
    return { baselines: baselines || [], series: series || [] };
  }
  let prevSp = null, b = null;
  const series2 = series.map((row) => {
    const sp = Number(row.SP500);
    if (!Number.isFinite(sp) || sp <= 0) return row;            // bad S&P point → no beta value this row (rebase skips nulls)
    if (b == null) b = 1;                                       // anchor at $1 on the first valid S&P point
    else if (prevSp) b = b * (1 + SPY_BETA * (sp / prevSp - 1));
    prevSp = sp;
    return { ...row, SP500_beta: Math.round(b * 1e6) / 1e6 };
  });
  const baselines2 = [...baselines, {
    key: "SP500_beta", label: `S&P 500 ×${SPY_BETA} (β-matched)`, color: "#5b6b76",
    desc: `A passive S&P 500 position scaled to the strategy's historical market beta (β≈${SPY_BETA} to SPY, from the Beta study) — the FAIR bar for the hedged long/short book, not a fully-invested S&P.`,
  }];
  return { baselines: baselines2, series: series2 };
}

export function BaselineProvider({ children }) {
  const [available, setAvailable] = useState([]);
  const [series, setSeries] = useState([]);
  const [selected, setSelected] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => {
    api("baselines").then((d) => {
      const { baselines, series } = withBetaMatched(d.baselines || [], d.series || []);
      setAvailable(baselines); setSeries(series);
    }).catch(() => {});
  }, []);
  useEffect(() => { try { localStorage.setItem(KEY, JSON.stringify(selected)); } catch {} }, [selected]);
  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  return <Ctx.Provider value={{ available, series, selected, toggle }}>{children}</Ctx.Provider>;
}
export const useBaselines = () => useContext(Ctx);

// the SSOT yearFrac (dates.js) — a local copy here once drifted from the chart formula (G-10)
const yf = yearFrac;

function nearest(primaryPts, t) {
  let best = primaryPts[0];
  for (const p of primaryPts) if (Math.abs(p.t - t) < Math.abs(best.t - t)) best = p;
  return best.v;
}

// primaryPts: [{t, v}] sorted by t (the chart's main curve). Returns selected
// baselines as rebased {key,label,color,pts:[{t,v}]} ready to map with the
// chart's own mx/my.
export function rebaseBaselines(primaryPts, { series, selected, available }) {
  if (!primaryPts?.length || !selected?.length || !series?.length) return [];
  const meta = Object.fromEntries(available.map((b) => [b.key, b]));
  const t0 = primaryPts[0].t, t1 = primaryPts[primaryPts.length - 1].t;
  const out = [];
  for (const key of selected) {
    if (!meta[key]) continue;
    let anchor = null;
    for (const row of series) {
      const v = row[key]; if (v == null) continue;
      const t = yf(row.date); if (t < t0 - 1e-9 || t > t1 + 1e-9) continue;   // anchor at/after the window start
      anchor = { t, v }; break;
    }
    if (!anchor) continue;
    const scale = nearest(primaryPts, anchor.t) / anchor.v;
    const pts = []; let lastDate = null;
    for (const row of series) {
      const v = row[key]; if (v == null) continue;
      // CLIP to the account window [t0, t1] — a ±0.5yr tolerance drew baselines far left/right of the
      // axis when the account history is short (e.g. a 12-day-old book). Never draw before t0 / past t1.
      const t = yf(row.date); if (t < t0 - 1e-9 || t > t1 + 1e-9) continue;
      pts.push({ t, v: v * scale }); lastDate = row.date;
    }
    if (pts.length > 1) {
      // The account carries a LIVE same-day point, but benchmarks publish on a one-trading-day
      // lag (their last point is the last market close). Carry the last close flat for AT MOST ONE
      // trading day (how a benchmark is quoted intraday) — then the line STOPS and the legend says
      // "to {date}" (F6, W-29: an unbounded LOCF drew weeks of a frozen benchmark as calm).
      const last = pts[pts.length - 1];
      const carryTo = Math.min(t1, yf(nextSession(lastDate)));
      const truncated = carryTo < t1 - 1e-9;
      if (last.t < carryTo - 1e-9) pts.push({ t: carryTo, v: last.v, carried: true });
      out.push({ key, label: truncated ? `${meta[key].label} · to ${lastDate}` : meta[key].label,
                 color: meta[key].color, pts, end: lastDate, truncated });
    }
  }
  return out;
}
